import moment from 'moment';
import { PropTypes } from 'prop-types';
import { useContext, useEffect, useState, useMemo } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import AccionaTableTabs from '../../../../../components/AccionaTableTabs';
import RemoveModal from '../../../../../components/RemoveModal/index';
import { MainContext } from '../../../../../context/MainContext';
import { getScannedDocumentByMysqlIDAndPid } from '../../../../../services/api/scannedDocuments';
import * as noValidatedPCScannedDocumentsActions from '../../../../../services/redux/scannedDocumentsPC/noValidatedPCScannedDocuments/actions';
import * as validatedPCScannedDocumentsActions from '../../../../../services/redux/scannedDocumentsPC/validatedPCScannedDocuments/actions';
import {
  BUTTONS_NO_VALIDATED,
  BUTTONS_VALIDATED,
  COLUMNS_HEADERS_VALIDATED,
  getNoValidatedHeadersColumns,
} from './columns';

const Tabs = {
  VALIDATED: 'validated',
  NO_VALIDATED: 'noValidated',
};

const ScannedDocumentsList = ({
  getValidatedScannedDocumentsList,
  getNoValidatedScannedDocumentsList,
  deleteValidatedScannedDocument,
  deleteNoValidatedScannedDocument,
  errorMessages,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { idDetail: id, id: pid, validated } = useParams();
  const { project } = useOutletContext();
  const [docketFilter, setDocketFilter] = useState(null);
  const { setLoading, setError } = useContext(MainContext);
  const [selectedTab, setSelectedTab] = useState(validated);
  const [loadingScannedDocumentByMysql, seLoadingScannedDocumentByMysql] =
    useState(Boolean(id));
  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const initialFilters = useMemo(
    () => (docketFilter ? { docket_number: docketFilter } : undefined),
    [docketFilter],
  );
  const { me } = useSelector(({ auth }) => auth);

  const initialFiltersNotValidated = useMemo(
    () => ({ sortable: 'fileName-ASC' }),
    [],
  );

  const onClickDeleteIcon = (scannedDocument) => {
    setSelectedRows([scannedDocument]);
    setShowRemoveModal(true);
  };
  const onClickEditIcon = (scannedDocument, isValidated) => {
    navigate(
      `/app/project/${
        project.id_project
      }/quality/ocr/scannedDocuments/scannedDocumentsPC/update/${
        scannedDocument.model
      }/${scannedDocument.id}/${scannedDocument.fileName}${
        scannedDocument.model.includes('delivery') ? `/${selectedTab}` : ''
      }?${isValidated ? 'validated' : 'noValidated'}`,
    );
  };
  const onClickDataIcon = (scannedDocument) =>
    navigate(
      `/app/project/${project.id_project}/quality/ocr/scannedDocuments/scannedDocumentsPC/document/${scannedDocument.model}/${scannedDocument.id}/${scannedDocument.mysqlId}/${scannedDocument.fileName}`,
    );

  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    if (selectedTab === 'validated') {
      deleteValidatedScannedDocument(selectedRows);
    } else {
      deleteNoValidatedScannedDocument(selectedRows);
    }
    setSelectedRows([]);
  };

  const onTabChange = (tab) => {
    setSelectedTab(tab);
    navigate(`../${tab}`);
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [selectedTab]);

  useEffect(() => {
    if (errorMessages) {
      setError({
        status: 500,
        errorType: 'GET_SCANNED_DOCUMENT_ERROR',
      });
    }
  }, [errorMessages, setError]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      seLoadingScannedDocumentByMysql(true);
      const [sdData] = (
        await getScannedDocumentByMysqlIDAndPid(id, pid, 'delivery')
      ).data.data;
      if (sdData) {
        setDocketFilter(sdData.docket_number);
      }
      setLoading(false);
      seLoadingScannedDocumentByMysql(false);
    };
    if (id) {
      fetchData().catch((error) => {
        setLoading(false);
        setError({
          status: 500,
          errorType: 'GET_SCANNED_DOCUMENT_ERROR',
        });
      });
    }
  }, [id, navigate, setError, setLoading, pid]);

  if (id && loadingScannedDocumentByMysql) {
    return null;
  }

  return (
    <>
      <AccionaTableTabs
        selectedTab={selectedTab}
        setSelectedTab={onTabChange}
        tabs={[Tabs.VALIDATED, Tabs.NO_VALIDATED]}
        title={intl.formatMessage({
          id: 'pages.quality.ocr.scannedDocuments.scannedDocumentsPC',
        })}
        actions={
          <>
            <AccionaButton
              id="deleteBtn"
              disabled={!selectedRows.length || !me.is_active}
              onClick={() => {
                setShowRemoveModal(true);
              }}
              msg={intl.formatMessage({ id: 'button.delete' })}
              classIcon="icon fas fa-trash-alt"
            />
          </>
        }
        render={() => {
          switch (selectedTab) {
            case Tabs.VALIDATED:
              return (
                <AccionaTable
                  showTitle={false}
                  setLoading={setLoading}
                  allowClear
                  allowClearStyle={{
                    position: 'absolute',
                    top: -61,
                    right: 15,
                  }}
                  onClearFilter={setDocketFilter}
                  initialFilters={initialFilters}
                  key={Tabs.VALIDATED}
                  columns={COLUMNS_HEADERS_VALIDATED}
                  getItems={getValidatedScannedDocumentsList}
                  entity="validatedPCScannedDocuments"
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  isSelectionDisabled={(record) => {
                    return record.isLotified || record.isAssayed;
                  }}
                  isRowChecked={(record) => {
                    return selectedRows.some((r) => r.id === record.id);
                  }}
                  dataParseFunction={(v) => {
                    return v.map((e) => ({
                      ...e,
                      recipe: e.delivery_note_data.recipe,
                    }));
                  }}
                  actions={(record) => {
                    return (
                      <>
                        <AccionaTableButton
                          onClick={() => onClickDataIcon(record)}
                          type="view"
                          toolTip={intl.formatMessage({
                            id: BUTTONS_VALIDATED.viewAction.tooltip,
                          })}
                        />
                        <AccionaTableButton
                          onClick={() => onClickEditIcon(record, true)}
                          type="edit"
                          disabled={!me?.is_active}
                          toolTip={intl.formatMessage({
                            id: BUTTONS_VALIDATED.editAction.tooltip,
                          })}
                        />
                        <AccionaTableButton
                          onClick={() => onClickDeleteIcon(record)}
                          type="delete"
                          disabled={!me?.is_active}
                          toolTip={intl.formatMessage({
                            id: BUTTONS_VALIDATED.deleteAction.tooltip,
                          })}
                        />
                      </>
                    );
                  }}
                />
              );
            case Tabs.NO_VALIDATED:
              return (
                <AccionaTable
                  key={Tabs.NO_VALIDATED}
                  columns={getNoValidatedHeadersColumns(intl)}
                  initialFilters={initialFiltersNotValidated}
                  getItems={getNoValidatedScannedDocumentsList}
                  entity="noValidatedPCScannedDocuments"
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  showTitle={false}
                  dataParseFunction={(nv) => {
                    return nv.map((e) => ({
                      ...e,
                      createdAt: moment(e.createdAt, 'DD-MM-YYYY'),
                    }));
                  }}
                  actions={(nv) => (
                    <>
                      <AccionaTableButton
                        onClick={() => onClickEditIcon(nv, false)}
                        type="edit"
                        disabled={!me?.is_active}
                        toolTip={intl.formatMessage({
                          id: BUTTONS_NO_VALIDATED.editAction.tooltip,
                        })}
                      />
                      <AccionaTableButton
                        onClick={() => onClickDeleteIcon(nv)}
                        type="delete"
                        disabled={!me?.is_active}
                        toolTip={intl.formatMessage({
                          id: BUTTONS_NO_VALIDATED.deleteAction.tooltip,
                        })}
                      />
                    </>
                  )}
                />
              );
            default:
              return null;
          }
        }}
      />
      <RemoveModal
        entity="scannedDocuments"
        selectedRows={selectedRows}
        msg={intl.formatMessage({
          id:
            selectedRows.length > 1
              ? 'delete.scannedDocuments'
              : 'delete.scannedDocument',
        })}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
    </>
  );
};

const mapStateToProps = ({
  validatedPCScannedDocuments,
  noValidatedPCScannedDocuments,
}) => ({
  errorMessages:
    validatedPCScannedDocuments.errorMessages ||
    noValidatedPCScannedDocuments.errorMessages,
});

const matchDispachToProps = {
  getValidatedScannedDocumentsList:
    validatedPCScannedDocumentsActions.getValidatedScannedDocuments,
  getNoValidatedScannedDocumentsList:
    noValidatedPCScannedDocumentsActions.getNoValidatedScannedDocuments,
  deleteNoValidatedScannedDocument:
    noValidatedPCScannedDocumentsActions.deleteNoValidatedScannedDocument,
  deleteValidatedScannedDocument:
    validatedPCScannedDocumentsActions.deleteValidatedScannedDocument,
};

ScannedDocumentsList.defaultProps = {
  errorMessages: {},
  selectedRows: [],
};

ScannedDocumentsList.propTypes = {
  getValidatedScannedDocumentsList: PropTypes.func.isRequired,
  getNoValidatedScannedDocumentsList: PropTypes.func.isRequired,
  deleteValidatedScannedDocument: PropTypes.func.isRequired,
  deleteNoValidatedScannedDocument: PropTypes.func.isRequired,
  errorMessages: PropTypes.shape({
    status: PropTypes.number,
  }),
  selectedRows: PropTypes.arrayOf(PropTypes.object),
};

export default connect(
  mapStateToProps,
  matchDispachToProps,
)(injectIntl(ScannedDocumentsList));
